import { useMemo } from 'react';

import {
  CYLINDO_OPTIONS,
  CYLINDO_ACCOUNT_ID,
  FABRIC_PRODUCT_OPTION_ID,
  LEATHER_PRODUCT_OPTION_ID,
  PANTONE_FABRIC_CONFIG,
} from 'commons/constants';
import { orientationOptionIds } from 'components/consumer/OrientationPDPModal';
import usePDPOptions from 'components/consumer/PDP/state/context/usePDPOptions';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';
import useCylindoDimensionShots from '../useCylindoDimensionShots';
import useCylindoCache from './cylindoCache/useCylindoCache';
import {
  DISABLED_UPHOLSTERY,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  SUNBRELLA_NAME_MISMATCH_OPTIONS,
  CYLINDO_FRAMES_TO_SHOW,
  CYLINDO_CONTENT_API,
} from './cylindoCache/defaults';

const usePDPCylindoViewer = ({
  isPDPDesktop = false,
  material = null,
  deferCylindoFetch = false,
} = {}) => {
  const { data: basicDetails } = usePDPBasicDetails();

  const {
    data: { selectedOptionsExtended },
  } = usePDPOptions();

  const productId = basicDetails?.id;
  const isCylindoProduct = !!basicDetails?.custom_properties?.cylindo_product;

  const {
    config: cylindoProductConfig,
    version: cylindoVersion,
    loading,
  } = useCylindoCache(productId, isCylindoProduct, deferCylindoFetch);

  const nameMismatchOptions = useMemo(
    () => ({
      ...SUNBRELLA_NAME_MISMATCH_OPTIONS,
      ...PANTONE_FABRIC_CONFIG.pantoneFabricCylindoNameMapping,
    }),
    []
  );

  const upholsteryKey = useMemo(() => {
    if (!productId || !cylindoProductConfig || !selectedOptionsExtended) {
      return null;
    }

    const upholsteryName =
      material?.label ??
      selectedOptionsExtended[FABRIC_PRODUCT_OPTION_ID]?.value?.value ??
      selectedOptionsExtended[LEATHER_PRODUCT_OPTION_ID]?.value?.value ??
      null;

    if (!upholsteryName) {
      return null;
    }

    const correctedUpholsteryName =
      nameMismatchOptions[upholsteryName] || upholsteryName;

    return correctedUpholsteryName
      .trim()
      .replace('™', '')
      .toUpperCase();
  }, [
    cylindoProductConfig,
    material?.label,
    nameMismatchOptions,
    productId,
    selectedOptionsExtended,
  ]);

  const hasUpholsteryOption = !!upholsteryKey;

  const dimensionShotsData = useCylindoDimensionShots({
    upholsteryKey,
    cylindoVersion,
  });

  const upholsteryFeatureDetails = useMemo(() => {
    if (!upholsteryKey || DISABLED_UPHOLSTERY.includes(upholsteryKey)) {
      return undefined;
    }

    const upholsteryFeature = cylindoProductConfig.features?.find(
      feat => feat.code === CYLINDO_OPTIONS.UPHOLSTERY
    );

    const upholsteryOption = upholsteryFeature?.options.find(
      opt => opt.code?.toUpperCase() === upholsteryKey
    );

    if (!upholsteryOption) {
      return undefined;
    }

    return {
      [CYLINDO_OPTIONS.UPHOLSTERY]: upholsteryOption.code,
    };
  }, [cylindoProductConfig, upholsteryKey]);

  const isUpholsteryFeatureAvailable = !!upholsteryFeatureDetails;

  const orientationKey = useMemo(() => {
    const orientationId = orientationOptionIds.find(
      id => selectedOptionsExtended[id]
    );

    const orientationValue =
      selectedOptionsExtended[orientationId]?.value?.value;

    return orientationValue?.toUpperCase();
  }, [selectedOptionsExtended]);

  const hasOrientationOption = !!orientationKey;

  const orientationOptionDetails = useMemo(() => {
    if (!orientationKey) {
      return undefined;
    }

    const orientationFeature = cylindoProductConfig?.features?.find(
      feat => feat.code === CYLINDO_OPTIONS.ORIENTATION
    );
    const orientationKeyStr = String(orientationKey)
      .trim()
      .toLowerCase();
    const orientationOption = orientationFeature?.options?.find(
      opt =>
        String(opt.code)
          .trim()
          .toLowerCase() === orientationKeyStr
    );
    if (!orientationOption) {
      return undefined;
    }

    return {
      [CYLINDO_OPTIONS.ORIENTATION]: orientationOption.code,
    };
  }, [cylindoProductConfig?.features, orientationKey]);

  const isOrientationFeatureAvailable = !!orientationOptionDetails;

  const areAllFeaturesAvailable =
    (!hasUpholsteryOption ||
      (hasUpholsteryOption && isUpholsteryFeatureAvailable)) &&
    (!hasOrientationOption ||
      (hasOrientationOption && isOrientationFeatureAvailable));

  const cylindoFeatures = useMemo(() => {
    if (!upholsteryFeatureDetails && !orientationOptionDetails) {
      return EMPTY_OBJECT;
    }
    return {
      ...(upholsteryFeatureDetails || EMPTY_OBJECT),
      ...(orientationOptionDetails || EMPTY_OBJECT),
    };
  }, [orientationOptionDetails, upholsteryFeatureDetails]);

  const cylindoConfigurationPhotos = useMemo(() => {
    if (
      isPDPDesktop &&
      areAllFeaturesAvailable &&
      Object.entries(cylindoFeatures).length
    ) {
      const featuresString = Object.entries(cylindoFeatures)
        .map(([featureKey, featureCode]) => `${featureKey}:${featureCode}`)
        .join('&feature=');

      return CYLINDO_FRAMES_TO_SHOW.map((frame, index) => ({
        id: frame,
        index,
        src: `${CYLINDO_CONTENT_API}/${CYLINDO_ACCOUNT_ID}/products/${productId}/frames/${frame}?feature=${featuresString}`,
        type: 'cylindo',
      }));
    }

    return EMPTY_ARRAY;
  }, [areAllFeaturesAvailable, cylindoFeatures, isPDPDesktop, productId]);

  return {
    cylindoConfigurationPhotos,
    cylindoFeatures,
    isCylindoAvailable: !!cylindoProductConfig && areAllFeaturesAvailable,
    loading,
    isCylindoConfigurationPhotos:
      isCylindoProduct &&
      !!cylindoProductConfig &&
      areAllFeaturesAvailable &&
      cylindoConfigurationPhotos?.length > 0,
    upholsteryKey,
    ...dimensionShotsData,
  };
};

export default usePDPCylindoViewer;
